import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";

import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle, MapPin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { getServices, getStats, getVideoProjects, getTestimonials, getCompanyInfo } from "../services/api";
import { serviceAreas } from "../mockData";
import { HomeHero } from "../components/HomeHero";

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState([]);
  const [videoProjects, setVideoProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({
    phone: "1-888-907-3777",
    email: "hello@groundhogs.ca"
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, statsData, videoProjectsData, testimonialsData, companyInfoData] = await Promise.all([
          getServices(),
          getStats(),
          getVideoProjects(),
          getTestimonials(),
          getCompanyInfo()
        ]);
        
        setServices(servicesData);
        setStats(statsData);
        setVideoProjects(videoProjectsData);
        setTestimonials(testimonialsData);
        setCompanyInfo(companyInfoData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="gh-home" data-testid="home-page">
      <SEO 
        title="Septic Installers Thunder Bay | Groundhogs Septics"
        description="Thunder Bay's fastest specialist septic installers. 3-day average engineered installations, 1,200+ systems, 100% permit approval. No pumping — design, installation & replacement only. Free estimate: 1-888-907-3777."
        keywords="septic installers near me, septic installation Thunder Bay, septic system installers Thunder Bay, septic system replacement Thunder Bay, Eljen system, traditional septic"
      />
      <Header />

      {/* Hero Section */}
      <HomeHero phone={companyInfo.phone} />

      {/* Why Best Installers Section */}
      <section className="gh-home-stats py-12" data-testid="home-statistics" style={{ backgroundColor: "var(--primary-orange)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-6">
            Why We&apos;re Thunder Bay&apos;s Best Septic Installers
          </h2>
          <div className="gh-stat-grid grid grid-cols-2 md:grid-cols-6 gap-6" data-reveal>
            <div>
              <div className="text-4xl font-bold mb-2">3 Days Avg</div>
              <div className="text-sm">Installation Time</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">1,200+</div>
              <div className="text-sm">Successful Installs</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">15+</div>
              <div className="text-sm">Years Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">99%</div>
              <div className="text-sm">Customer Satisfaction</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-sm">Permit Approval</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">5-Year</div>
              <div className="text-sm">Warranty</div>
            </div>
          </div>
        </div>
      </section>

      {/* Permit Expertise Section */}
      <section className="gh-home-expertise py-16" data-testid="home-expertise" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold" style={{ color: "var(--dark-navy)" }}>
              Expedited Permit Processing & Engineering Expertise
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                Expert Engineering Partners
              </h3>
              <p style={{ color: "var(--text-dark)" }}>
                We work with Thunder Bay&apos;s top septic engineers and technicians who specialize in expedited permit processing. 
                Our team has decades of combined experience navigating Ontario Building Code and municipal requirements.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                Ministry of Environment Approvals
              </h3>
              <p style={{ color: "var(--text-dark)" }}>
                Successfully handled complex sites requiring Ministry of Environment approval. Even for challenging properties 
                near water bodies or sensitive areas, our engineering team gets permits approved efficiently.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg" style={{ backgroundColor: "var(--light-gray)" }}>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                <CheckCircle size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                100% Permit Success Rate
              </h3>
              <p style={{ color: "var(--text-dark)" }}>
                Every septic installation we&apos;ve designed has received permit approval. Our thorough site evaluation 
                and engineering expertise means no delays, no rejections, no headaches.
              </p>
            </div>
          </div>
          <div className="mt-12 max-w-3xl mx-auto p-8 rounded-lg" style={{ backgroundColor: "var(--primary-orange)" }}>
            <h3 className="text-2xl font-bold mb-4 text-white text-center">
              Tricky Site? Challenging Location? We&apos;ve Got You Covered.
            </h3>
            <p className="text-white text-center mb-4">
              While other installers walk away from difficult properties, we specialize in solving complex septic challenges. 
              High water tables, rocky terrain, Ministry of Environment requirements, waterfront properties - we&apos;ve successfully 
              permitted and installed systems that others said were impossible.
            </p>
            <div className="text-center">
              <Link
                to="/septic-system-design"
                className="inline-flex items-center space-x-2 px-8 py-3 rounded-md font-bold bg-white hover:opacity-90"
                style={{ color: "var(--primary-orange)" }}
              >
                <span>Learn About Our Design & Permit Services</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Project Gallery Section */}
      <section className="gh-home-projects py-16" data-testid="home-projects" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              See Our Work in Action
            </h2>
            <p className="text-lg" style={{ color: "var(--text-light)" }}>
              Real projects from our expert septic installation team
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {videoProjects.map((project) => (
              <div key={project.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl" style={{ transition: "box-shadow 0.3s ease" }}>
                <div className="relative h-64 bg-gray-200 overflow-hidden">
                  <img 
                    src={project.thumbnail} 
                    alt={project.title} 
                    className="w-full h-full object-cover hover:scale-105" 
                    style={{ transition: "transform 0.3s ease" }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: "var(--dark-navy)" }}>
                    {project.title}
                  </h3>
                  <p className="text-sm" style={{ color: "var(--text-light)" }}>
                    {project.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="gh-home-areas py-16" data-testid="home-service-areas" style={{ backgroundColor: "var(--dark-navy)", color: "var(--white)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Serving Northwestern Ontario
            </h2>
            <p className="text-xl">
              15+ Years of Septic Installation Excellence Throughout the Region
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            {serviceAreas.slice(0, 1).map((area, index) => (
              <div
                key={index}
                className="p-8 rounded-lg"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
              >
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--primary-orange)" }}>
                    <MapPin size={32} />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-2 text-center">{area.name}</h3>
                {area.isHQ && (
                  <p className="text-center font-semibold mb-3" style={{ color: "var(--primary-orange)" }}>
                    Headquarters:
                  </p>
                )}
                <p className="text-center mb-4 opacity-80">{area.description}</p>
                <ul className="space-y-2">
                  {area.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/service-areas/${area.name.toLowerCase().replace(/ /g, '-')}`}
                  className="block mt-6 text-center px-6 py-3 rounded-md font-medium border-2 hover:opacity-80"
                  style={{ borderColor: "var(--primary-orange)", color: "var(--primary-orange)" }}
                >
                  {area.name} Services
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-lg mb-4">No matter which area you&apos;re in, you get the same Groundhogs™ advantage:</p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center space-x-2">
                <CheckCircle style={{ color: "var(--primary-orange)" }} />
                <span>15+ Years Experience</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle style={{ color: "var(--primary-orange)" }} />
                <span>1200+ Installs</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle style={{ color: "var(--primary-orange)" }} />
                <span>Eljen Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle style={{ color: "var(--primary-orange)" }} />
                <span>Elite Crews</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planned expansion, separate from currently served areas */}
      <section
        aria-labelledby="sudbury-announcement-heading"
        data-testid="sudbury-announcement"
        className="py-8"
        style={{ backgroundColor: "var(--light-gray)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto border-l-4 pl-5 sm:pl-6" style={{ borderColor: "var(--primary-orange)" }}>
            <h2
              id="sudbury-announcement-heading"
              data-testid="sudbury-announcement-heading"
              className="text-base md:text-lg font-bold mb-2"
              style={{ color: "var(--dark-navy)" }}
            >
              Coming Soon: Septic Services in Sudbury, ON!
            </h2>
            <p data-testid="sudbury-announcement-description" className="text-sm md:text-base leading-relaxed" style={{ color: "var(--text-dark)" }}>
              Groundhogs™ Septics is planning to bring septic installation and repair services to Sudbury, Ontario. Services are not yet available in Sudbury. Stay tuned for launch updates.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section className="gh-home-services py-16 bg-white" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Core Septic Installation Services
            </h2>
            <p className="text-xl" style={{ color: "var(--text-light)" }}>
              Systemized Solutions. Built to Last.
            </p>
            <p className="mt-4 max-w-3xl mx-auto" style={{ color: "var(--text-dark)" }}>
              Our reputation is built on precision, reliability, and results that stand the test of time. 
              We specialize in new septic system installations and complete replacements, serving homeowners 
              across Northwestern Ontario.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl" style={{ transition: "box-shadow 0.3s ease" }}>
                <div className="h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-105"
                    style={{ transition: "transform 0.3s ease" }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                    {service.title}
                  </h3>
                  <p className="mb-4" style={{ color: "var(--text-light)" }}>
                    {service.description}
                  </p>
                  <Link
                    to={`/services/${service.slug}`}
                    className="inline-flex items-center space-x-2 font-medium hover:opacity-70"
                    style={{ color: "var(--primary-orange)" }}
                  >
                    <span>Learn More</span>
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="gh-home-testimonials py-16" data-testid="home-testimonials" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              What Satisfied Clients Say
            </h2>
            <p className="text-lg" style={{ color: "var(--text-light)" }}>
              Don&apos;t just take our word for it
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-8 rounded-lg shadow-md">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} style={{ color: "var(--primary-orange)" }}>★</span>
                  ))}
                </div>
                <p className="mb-6 italic" style={{ color: "var(--text-dark)" }}>
                  &quot;{testimonial.text}&quot;
                </p>
                <div>
                  <div className="font-bold" style={{ color: "var(--dark-navy)" }}>
                    {testimonial.name}
                  </div>
                  <div className="text-sm" style={{ color: "var(--text-light)" }}>
                    {testimonial.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affordable Pricing Section */}
      <section className="gh-home-pricing py-16" data-testid="home-pricing" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Affordable Septic Installation Thunder Bay
            </h2>
            <p className="text-xl" style={{ color: "var(--text-light)" }}>
              Quality septic system installation at competitive prices
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
                Traditional Septic Systems
              </h3>
              <div className="text-4xl font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                $15,000 - $60,000
              </div>
              <p className="mb-4" style={{ color: "var(--text-light)" }}>
                Complete septic tank and field installation for standard residential properties
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>Complete site evaluation</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>All permits & inspections</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>Septic tank installation</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>Drain field installation</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>5-year warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
                Eljen Mound Systems
              </h3>
              <div className="text-4xl font-bold mb-2" style={{ color: "var(--primary-orange)" }}>
                $20,000 - $50,000
              </div>
              <p className="mb-4" style={{ color: "var(--text-light)" }}>
                Advanced Eljen systems ideal for limited space properties. Much smaller field size requirements reduce imported sand and minimize site disruption.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>Much smaller field size required</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>Reduces imported sand</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>Ideal for limited space properties</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>High water table solutions</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle size={18} style={{ color: "var(--primary-orange)" }} />
                  <span>Factory-certified installation</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-lg mb-4" style={{ color: "var(--text-dark)" }}>
              <strong>Financing Available</strong> - In specific scenarios, we offer in-house financing and payment plans to make your septic system installation affordable.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 rounded-md font-bold text-white hover:opacity-90"
              style={{ backgroundColor: "var(--primary-orange)" }}
            >
              Get Your Free Estimate
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Resources - Internal Linking Section */}
      <section className="gh-home-resources py-16" data-testid="home-resources" style={{ backgroundColor: "var(--light-gray)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
              Learn More About Septic Installation
            </h2>
            <p className="text-xl" style={{ color: "var(--text-light)" }}>
              Helpful resources and guides for Northwestern Ontario property owners
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg" style={{ transition: "box-shadow 0.3s ease" }}>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                Cost Guides
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/blog/septic-installation-cost-thunder-bay-2025" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Septic Installation Costs 2025
                  </Link>
                </li>
                <li>
                  <Link to="/septic-tank-installation" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Septic Tank Installation Guide
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Pricing & Payment FAQ
                  </Link>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg" style={{ transition: "box-shadow 0.3s ease" }}>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                System Comparisons
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/blog/traditional-vs-eljen-septic-systems-thunder-bay" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Traditional vs Eljen Systems
                  </Link>
                </li>
                <li>
                  <Link to="/services/eljen-mound-installation" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Eljen Mound System Details
                  </Link>
                </li>
                <li>
                  <Link to="/commercial-septic-systems" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Commercial Septic Solutions
                  </Link>
                </li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg" style={{ transition: "box-shadow 0.3s ease" }}>
              <h3 className="text-xl font-bold mb-3" style={{ color: "var(--dark-navy)" }}>
                Local Services
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/service-areas/thunder-bay" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Thunder Bay Installation
                  </Link>
                </li>
                <li>
                  <Link to="/service-areas/kenora" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → Kenora Area Services
                  </Link>
                </li>
                <li>
                  <Link to="/service-areas" className="hover:opacity-70" style={{ color: "var(--primary-orange)" }}>
                    → View All 53 Service Areas
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gh-home-cta py-16 bg-white" data-testid="home-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4" style={{ color: "var(--dark-navy)" }}>
            Ready for Your New Septic System Installation?
          </h2>
          <p className="text-xl mb-8" style={{ color: "var(--text-light)" }}>
            Call Thunder Bay&apos;s best septic installers for lasting solutions. Get your fast virtual estimate today!
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 rounded-md font-bold text-white text-lg hover:opacity-90"
            style={{ backgroundColor: "var(--primary-orange)" }}
          >
            Contact Us Now
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;